import { useCallback, useEffect, useMemo, useState } from "react";
import { format, subMonths, addMonths } from "date-fns";
import { es } from "date-fns/locale";
import {
  Boxes,
  CircleAlert,
  Download,
  Eye,
  LoaderCircle,
  MessageSquareText,
  RefreshCw,
  Save,
  Search,
  StickyNote,
  Trash2,
  Users
} from "lucide-react";
import { useSearchParams } from "react-router-dom";

import {
  deleteProfessionalBooking,
  fetchProfessionalBookingDetail,
  fetchProfessionalConsumedProducts,
  fetchProfessionalCustomerDetail,
  fetchProfessionalCustomerDocumentBlob,
  fetchProfessionalCustomerProducts,
  fetchProfessionalCustomers,
  fetchProfessionalExpiredProducts,
  fetchProfessionalProductSessions,
  fetchProfessionalServices,
  updateProfessionalBookingComment,
  updateProfessionalCustomerComment,
  updateProfessionalCustomerInfoNew,
  type ProfessionalBookingDetail,
  type ProfessionalCustomerBooking,
  type ProfessionalCustomerDetail,
  type ProfessionalCustomerListItem,
  type ProfessionalCustomerProduct,
  type ProfessionalProductConsumption,
  type ProfessionalService,
  type ProfessionalStatusProduct
} from "../lib/professionalCustomersApi";
import "../styles/clientes.css";

type CustomerTab = "reservas" | "productos" | "documentos" | "comentarios";

function normalizeError(error: unknown, fallback: string) {
  if (typeof error === "object" && error && "response" in error) {
    const responseError = (error as { response?: { data?: { error?: string } } }).response?.data?.error;
    if (responseError) {
      return responseError;
    }
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return fallback;
}

function parseDate(value?: string | null) {
  if (!value) {
    return null;
  }
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(year, (month || 1) - 1, day || 1, 12, 0, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(value?: string | null) {
  const date = parseDate(value);
  return date ? format(date, "d MMM yyyy", { locale: es }) : "--";
}

function formatTime(value?: string | null) {
  if (!value) {
    return "--";
  }
  const [hours, minutes] = value.split(":");
  return hours && minutes ? `${hours}:${minutes}` : value;
}

function isVisibleClient(item: ProfessionalCustomerListItem) {
  const today = new Date();
  const minDate = subMonths(today, 2);
  const maxDate = addMonths(today, 1);
  const lastBooking = parseDate(item.last_booking_date);
  const nextBooking = parseDate(item.next_booking_date);

  return Boolean(
    (lastBooking && lastBooking >= minDate && lastBooking <= maxDate) ||
      (nextBooking && nextBooking >= minDate && nextBooking <= maxDate)
  );
}

function bookingIsFuture(booking: ProfessionalCustomerBooking) {
  const date = parseDate(booking.start_date);
  if (!date) {
    return false;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date >= today && String(booking.status || "").toLowerCase() !== "canceled";
}

function customerInitials(name: string) {
  const parts = name.split(" ").map((part) => part.trim()).filter(Boolean).slice(0, 2);
  if (!parts.length) {
    return "CL";
  }
  return parts.map((part) => part[0]?.toUpperCase() || "").join("");
}

function productTypeLabel(value?: string | null) {
  const key = String(value || "").toLowerCase();
  if (key === "single_session") return "Sesion unica";
  if (key === "multi_sessions") return "Bono";
  if (key === "recurrent") return "Recurrente";
  if (key === "membership") return "Membresia";
  return value || "Sin tipo";
}

export default function ClientesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCustomerId = Number(searchParams.get("customerId") || 0) || null;

  const [customers, setCustomers] = useState<ProfessionalCustomerListItem[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<CustomerTab>("reservas");
  const [detail, setDetail] = useState<ProfessionalCustomerDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generalComment, setGeneralComment] = useState("");
  const [infoNew, setInfoNew] = useState("");
  const [savingGeneral, setSavingGeneral] = useState(false);
  const [savingInfo, setSavingInfo] = useState(false);

  const [products, setProducts] = useState<ProfessionalCustomerProduct[]>([]);
  const [services, setServices] = useState<ProfessionalService[]>([]);
  const [expiredProducts, setExpiredProducts] = useState<ProfessionalStatusProduct[]>([]);
  const [consumedProducts, setConsumedProducts] = useState<ProfessionalStatusProduct[]>([]);
  const [bookingModal, setBookingModal] = useState<ProfessionalBookingDetail | null>(null);
  const [bookingCommentModal, setBookingCommentModal] = useState<ProfessionalCustomerBooking | null>(null);
  const [bookingCommentValue, setBookingCommentValue] = useState("");
  const [savingBookingComment, setSavingBookingComment] = useState(false);
  const [sessionsModal, setSessionsModal] = useState<ProfessionalProductConsumption[] | null>(null);
  const [loadingSessions, setLoadingSessions] = useState(false);

  const selectCustomer = useCallback((customerId: number) => {
    const next = new URLSearchParams(searchParams);
    next.set("customerId", String(customerId));
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  const loadCustomers = useCallback(async () => {
    setLoadingCustomers(true);
    setError(null);
    try {
      const rows = await fetchProfessionalCustomers();
      setCustomers(rows);
    } catch (requestError) {
      setError(normalizeError(requestError, "No se pudieron cargar los clientes"));
    } finally {
      setLoadingCustomers(false);
    }
  }, []);

  useEffect(() => {
    void loadCustomers();
  }, [loadCustomers]);

  const visibleCustomers = useMemo(() => {
    const query = search.trim().toLowerCase();
    return customers
      .filter((item) => isVisibleClient(item) || item.user_id === selectedCustomerId)
      .filter((item) => !query || item.name.toLowerCase().includes(query));
  }, [customers, search, selectedCustomerId]);

  useEffect(() => {
    if (selectedCustomerId || !visibleCustomers.length) {
      return;
    }

    selectCustomer(visibleCustomers[0].user_id);
  }, [selectedCustomerId, selectCustomer, visibleCustomers]);

  const loadDetail = useCallback(async (customerId: number) => {
    setLoadingDetail(true);
    setError(null);
    try {
      const nextDetail = await fetchProfessionalCustomerDetail(customerId);
      setDetail(nextDetail);
      setGeneralComment(nextDetail.comentario || "");
      setInfoNew(nextDetail.info_nuevos_clientes || "");
    } catch (requestError) {
      setDetail(null);
      setError(normalizeError(requestError, "No se pudo cargar el detalle del cliente"));
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedCustomerId) {
      return;
    }
    void loadDetail(selectedCustomerId);
  }, [loadDetail, selectedCustomerId]);

  useEffect(() => {
    if (!detail || tab !== "productos") {
      return;
    }

    let cancelled = false;
    async function loadProductsData() {
      setLoadingProducts(true);
      try {
        const [activeRows, serviceRows, expiredRows, consumedRows] = await Promise.all([
          fetchProfessionalCustomerProducts(detail.user_id),
          fetchProfessionalServices(),
          fetchProfessionalExpiredProducts(detail.user_id),
          fetchProfessionalConsumedProducts(detail.user_id)
        ]);

        if (!cancelled) {
          setProducts(activeRows);
          setServices(serviceRows);
          setExpiredProducts(expiredRows);
          setConsumedProducts(consumedRows);
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(normalizeError(requestError, "No se pudo cargar la informacion de productos"));
        }
      } finally {
        if (!cancelled) {
          setLoadingProducts(false);
        }
      }
    }

    void loadProductsData();
    return () => {
      cancelled = true;
    };
  }, [detail, tab]);

  async function handleSaveGeneralComment() {
    if (!detail) return;
    setSavingGeneral(true);
    setError(null);
    try {
      await updateProfessionalCustomerComment(detail.user_id, generalComment.trim() || null);
      await loadDetail(detail.user_id);
    } catch (requestError) {
      setError(normalizeError(requestError, "No se pudo guardar el comentario general"));
    } finally {
      setSavingGeneral(false);
    }
  }

  async function handleSaveInfoNew() {
    if (!detail) return;
    setSavingInfo(true);
    setError(null);
    try {
      await updateProfessionalCustomerInfoNew(detail.user_id, infoNew.trim() || null);
      await loadDetail(detail.user_id);
    } catch (requestError) {
      setError(normalizeError(requestError, "No se pudo guardar la nota informativa"));
    } finally {
      setSavingInfo(false);
    }
  }

  async function openBookingDetail(bookingId: number) {
    try {
      const data = await fetchProfessionalBookingDetail(bookingId);
      setBookingModal(data);
    } catch (requestError) {
      setError(normalizeError(requestError, "No se pudo cargar el detalle de la reserva"));
    }
  }

  async function saveBookingComment() {
    if (!bookingCommentModal || !detail) return;
    setSavingBookingComment(true);
    setError(null);
    try {
      await updateProfessionalBookingComment(bookingCommentModal.booking_id, bookingCommentValue);
      setBookingCommentModal(null);
      setBookingCommentValue("");
      await loadDetail(detail.user_id);
    } catch (requestError) {
      setError(normalizeError(requestError, "No se pudo guardar el comentario de la reserva"));
    } finally {
      setSavingBookingComment(false);
    }
  }

  async function removeBooking(bookingId: number) {
    if (!detail) return;
    setError(null);
    try {
      await deleteProfessionalBooking(bookingId);
      await loadDetail(detail.user_id);
    } catch (requestError) {
      setError(normalizeError(requestError, "No se pudo cancelar la reserva"));
    }
  }

  async function openProductSessions(product: ProfessionalStatusProduct) {
    if (!detail || !product.product_id) return;
    setLoadingSessions(true);
    try {
      const data = await fetchProfessionalProductSessions(detail.user_id, product.product_id, product.active_product_id);
      setSessionsModal(data);
    } catch (requestError) {
      setError(normalizeError(requestError, "No se pudo cargar el consumo del producto"));
    } finally {
      setLoadingSessions(false);
    }
  }

  async function openDocument(customerId: number, filename: string, mode: "preview" | "download") {
    try {
      const blob = await fetchProfessionalCustomerDocumentBlob(customerId, filename, mode === "preview");
      const url = URL.createObjectURL(blob);
      if (mode === "preview") {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = filename;
        anchor.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (requestError) {
      setError(normalizeError(requestError, "No se pudo abrir el documento"));
    }
  }

  const bookingsBySection = useMemo(() => {
    if (!detail) {
      return { future: [], past: [], canceled: [] } as const;
    }

    const active = [...detail.booked_bookings, ...detail.active_bookings, ...detail.completed_bookings];
    return {
      future: active.filter(bookingIsFuture),
      past: active.filter((booking) => !bookingIsFuture(booking)),
      canceled: detail.canceled_bookings
    };
  }, [detail]);

  const commentsHistory = useMemo(() => {
    if (!detail) return [];
    return [...detail.booked_bookings, ...detail.active_bookings, ...detail.completed_bookings, ...detail.canceled_bookings]
      .filter((booking) => booking.comment && booking.comment.trim())
      .sort((left, right) => `${right.start_date}${right.start_time || ""}`.localeCompare(`${left.start_date}${left.start_time || ""}`));
  }, [detail]);

  const serviceMap = useMemo(() => new Map(services.map((service) => [service.id, service.name])), [services]);

  return (
    <main className="shell-page">
      <section className="clients-hero shell-hero-card">
        <div className="shell-hero-icon">
          <Users size={22} />
        </div>
        <div className="shell-hero-copy">
          <span className="shell-eyebrow">Relacion con clientes</span>
          <h1>Clientes</h1>
          <p>Consulta los clientes asociados a tu actividad, revisa reservas, documentos, productos y guarda notas utiles sin salir de la app.</p>
        </div>
      </section>

      {error ? (
        <section className="clients-error-box">
          <CircleAlert size={18} />
          <span>{error}</span>
        </section>
      ) : null}

      <section className="clients-layout">
        <aside className="clients-list-panel">
          <div className="clients-list-header">
            <div className="clients-search">
              <Search size={16} />
              <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar cliente" />
            </div>
            <button type="button" className="clients-refresh-button" onClick={() => void loadCustomers()}>
              <RefreshCw size={16} />
            </button>
          </div>

          {loadingCustomers ? (
            <div className="clients-list-empty">Cargando clientes...</div>
          ) : visibleCustomers.length === 0 ? (
            <div className="clients-list-empty">No hay clientes visibles para este profesional.</div>
          ) : (
            <div className="clients-list">
              {visibleCustomers.map((customer) => (
                <button
                  key={customer.user_id}
                  type="button"
                  className={`clients-list-item ${selectedCustomerId === customer.user_id ? "active" : ""}`}
                  onClick={() => selectCustomer(customer.user_id)}
                >
                  <div className="clients-avatar">{customerInitials(customer.name)}</div>
                  <div className="clients-list-copy">
                    <strong>{customer.name}</strong>
                    <span>{customer.next_booking_date ? `Proxima ${formatDate(customer.next_booking_date)}` : "Sin proxima reserva"}</span>
                    <small>{customer.booking_count} reservas</small>
                  </div>
                </button>
              ))}
            </div>
          )}
        </aside>

        <section className="clients-detail-panel">
          {loadingDetail ? (
            <div className="clients-detail-empty">Cargando detalle...</div>
          ) : !detail ? (
            <div className="clients-detail-empty">Selecciona un cliente para ver su ficha completa.</div>
          ) : (
            <>
              <div className="clients-profile-card">
                <div className="clients-profile-main">
                  <div className="clients-profile-avatar">{customerInitials(detail.name)}</div>
                  <div>
                    <h2>{detail.name}</h2>
                    <div className="clients-profile-meta">
                      <span>{detail.email || "Sin email"}</span>
                      <span>{detail.phone || "Sin telefono"}</span>
                      <span>Alta {formatDate(detail.created_at?.slice(0, 10) || null)}</span>
                    </div>
                  </div>
                </div>

                <div className="clients-notes-grid">
                  <article className="clients-note-card">
                    <div className="clients-note-header">
                      <StickyNote size={16} />
                      <strong>Comentario general</strong>
                    </div>
                    <textarea value={generalComment} onChange={(event) => setGeneralComment(event.target.value)} />
                    <button type="button" className="clients-save-button" disabled={savingGeneral} onClick={() => void handleSaveGeneralComment()}>
                      {savingGeneral ? <LoaderCircle size={16} className="spin" /> : <Save size={16} />}
                      Guardar
                    </button>
                  </article>

                  <article className="clients-note-card">
                    <div className="clients-note-header">
                      <MessageSquareText size={16} />
                      <strong>Info nuevos clientes</strong>
                    </div>
                    <textarea value={infoNew} onChange={(event) => setInfoNew(event.target.value)} />
                    <button type="button" className="clients-save-button" disabled={savingInfo} onClick={() => void handleSaveInfoNew()}>
                      {savingInfo ? <LoaderCircle size={16} className="spin" /> : <Save size={16} />}
                      Guardar
                    </button>
                  </article>
                </div>
              </div>

              <div className="clients-tabs">
                {([
                  ["reservas", "Reservas"],
                  ["productos", "Productos"],
                  ["documentos", "Documentos"],
                  ["comentarios", "Comentarios"]
                ] as const).map(([key, label]) => (
                  <button key={key} type="button" className={tab === key ? "active" : ""} onClick={() => setTab(key)}>
                    {label}
                  </button>
                ))}
              </div>

              {tab === "reservas" ? (
                <div className="clients-tab-block">
                  {(["future", "past", "canceled"] as const).map((sectionKey) => {
                    const title =
                      sectionKey === "future" ? "Reservas futuras" : sectionKey === "past" ? "Reservas pasadas" : "Reservas canceladas";
                    const section = bookingsBySection[sectionKey];
                    return (
                      <section key={sectionKey} className="clients-booking-section">
                        <h3>{title}</h3>
                        {section.length === 0 ? (
                          <div className="clients-inline-empty">No hay reservas en esta categoria.</div>
                        ) : (
                          <div className="clients-booking-list">
                            {section.map((booking) => (
                              <article key={booking.booking_id} className="clients-booking-card">
                                <div>
                                  <strong>{booking.service_name || "Servicio"}</strong>
                                  <span>{booking.product_name || "Producto"}</span>
                                  <small>
                                    {formatDate(booking.start_date)} · {formatTime(booking.start_time)}
                                  </small>
                                </div>
                                <div className="clients-booking-actions">
                                  <button type="button" onClick={() => void openBookingDetail(booking.booking_id)}>
                                    Ver detalle
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setBookingCommentModal(booking);
                                      setBookingCommentValue(booking.comment || "");
                                    }}
                                  >
                                    Comentario
                                  </button>
                                  {sectionKey === "future" ? (
                                    <button type="button" className="danger" onClick={() => void removeBooking(booking.booking_id)}>
                                      <Trash2 size={14} />
                                      Cancelar
                                    </button>
                                  ) : null}
                                </div>
                              </article>
                            ))}
                          </div>
                        )}
                      </section>
                    );
                  })}
                </div>
              ) : null}

              {tab === "productos" ? (
                <div className="clients-tab-block">
                  {loadingProducts ? <div className="clients-inline-empty">Cargando productos...</div> : null}

                  {!loadingProducts ? (
                    <>
                      <section className="clients-product-section">
                        <h3>Productos activos</h3>
                        {products.length === 0 ? (
                          <div className="clients-inline-empty">No hay productos activos.</div>
                        ) : (
                          <div className="clients-product-list">
                            {products.map((product) => (
                              <article key={product.active_product_id} className="clients-product-card">
                                <div className="clients-product-icon">
                                  <Boxes size={18} />
                                </div>
                                <div>
                                  <strong>{product.name}</strong>
                                  <span>{serviceMap.get(product.service_ids[0]) || productTypeLabel(product.type_of_product)}</span>
                                  <small>{product.centro || "Centro no asignado"}</small>
                                </div>
                              </article>
                            ))}
                          </div>
                        )}
                      </section>

                      <section className="clients-product-section">
                        <h3>Productos caducados</h3>
                        {expiredProducts.length === 0 ? (
                          <div className="clients-inline-empty">No hay productos caducados.</div>
                        ) : (
                          <div className="clients-product-list">
                            {expiredProducts.map((product) => (
                              <article key={product.active_product_id} className="clients-product-card muted">
                                <div className="clients-product-icon">
                                  <Boxes size={18} />
                                </div>
                                <div>
                                  <strong>{product.product_name}</strong>
                                  <span>{productTypeLabel(product.type_of_product)}</span>
                                  <small>Caducado {formatDate(product.expiry_date)}</small>
                                </div>
                              </article>
                            ))}
                          </div>
                        )}
                      </section>

                      <section className="clients-product-section">
                        <h3>Productos consumidos</h3>
                        {consumedProducts.length === 0 ? (
                          <div className="clients-inline-empty">No hay productos totalmente consumidos.</div>
                        ) : (
                          <div className="clients-product-list">
                            {consumedProducts.map((product) => (
                              <button
                                key={product.active_product_id}
                                type="button"
                                className="clients-product-card action"
                                onClick={() => void openProductSessions(product)}
                              >
                                <div className="clients-product-icon">
                                  <Boxes size={18} />
                                </div>
                                <div>
                                  <strong>{product.product_name}</strong>
                                  <span>{productTypeLabel(product.type_of_product)}</span>
                                  <small>
                                    Sesiones {product.used_sessions || 0}/{product.total_session || 0}
                                  </small>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </section>
                    </>
                  ) : null}
                </div>
              ) : null}

              {tab === "documentos" ? (
                <div className="clients-tab-block">
                  {detail.documents.length === 0 ? (
                    <div className="clients-inline-empty">No hay documentos disponibles.</div>
                  ) : (
                    <div className="clients-document-list">
                      {detail.documents.map((document) => (
                        <article key={document.document_id} className="clients-document-card">
                          <div>
                            <strong>{document.document_name}</strong>
                            <small>{formatDate(document.created_at?.slice(0, 10) || null)}</small>
                          </div>
                          <div className="clients-document-actions">
                            <button type="button" onClick={() => void openDocument(detail.user_id, document.document, "preview")}>
                              <Eye size={14} />
                              Ver
                            </button>
                            <button type="button" onClick={() => void openDocument(detail.user_id, document.document, "download")}>
                              <Download size={14} />
                              Descargar
                            </button>
                          </div>
                        </article>
                      ))}
                    </div>
                  )}
                </div>
              ) : null}

              {tab === "comentarios" ? (
                <div className="clients-tab-block">
                  {commentsHistory.length === 0 ? (
                    <div className="clients-inline-empty">No hay comentarios registrados en reservas.</div>
                  ) : (
                    <div className="clients-comment-history">
                      {commentsHistory.map((booking) => (
                        <article key={booking.booking_id} className="clients-comment-card">
                          <div className="clients-comment-head">
                            <strong>{booking.service_name || "Reserva"}</strong>
                            <span>{formatDate(booking.start_date)}</span>
                          </div>
                          <p>{booking.comment}</p>
                        </article>
                      ))}
                    </div>
                  )}
                </div>
              ) : null}
            </>
          )}
        </section>
      </section>

      {bookingModal ? (
        <div className="clients-modal-backdrop" role="presentation" onClick={() => setBookingModal(null)}>
          <div className="clients-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <h3>Detalle de reserva</h3>
            <div className="clients-modal-grid">
              <span>Servicio</span><strong>{bookingModal.service_name || "--"}</strong>
              <span>Producto</span><strong>{bookingModal.product_name || "--"}</strong>
              <span>Fecha</span><strong>{formatDate(bookingModal.start_date)} · {formatTime(bookingModal.start_time)}</strong>
              <span>Estado</span><strong>{bookingModal.status}</strong>
              <span>Metodo pago</span><strong>{bookingModal.payment_method || "--"}</strong>
              <span>Comentario</span><strong>{bookingModal.comment || "--"}</strong>
            </div>
            <button type="button" className="clients-save-button" onClick={() => setBookingModal(null)}>Cerrar</button>
          </div>
        </div>
      ) : null}

      {bookingCommentModal ? (
        <div className="clients-modal-backdrop" role="presentation" onClick={() => setBookingCommentModal(null)}>
          <div className="clients-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <h3>Comentario de reserva</h3>
            <textarea
              className="clients-modal-textarea"
              value={bookingCommentValue}
              onChange={(event) => setBookingCommentValue(event.target.value)}
            />
            <div className="clients-modal-actions">
              <button type="button" onClick={() => setBookingCommentModal(null)}>Cancelar</button>
              <button type="button" className="primary" disabled={savingBookingComment} onClick={() => void saveBookingComment()}>
                {savingBookingComment ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {sessionsModal || loadingSessions ? (
        <div className="clients-modal-backdrop" role="presentation" onClick={() => setSessionsModal(null)}>
          <div className="clients-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <h3>Sesiones consumidas</h3>
            {loadingSessions ? (
              <div className="clients-inline-empty">Cargando...</div>
            ) : (
              <div className="clients-session-list">
                {(sessionsModal || []).flatMap((product) => product.sessions).map((session) => (
                  <div key={session.booking_id} className="clients-session-row">
                    <span>Sesion {session.booking_id}</span>
                    <strong>{formatDate(session.start_date)} · {formatTime(session.start_time)}</strong>
                  </div>
                ))}
              </div>
            )}
            <button type="button" className="clients-save-button" onClick={() => setSessionsModal(null)}>Cerrar</button>
          </div>
        </div>
      ) : null}
    </main>
  );
}
